import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { almacen, descargar } from '../api';
import {
  Boton,
  Cabecera,
  Cargando,
  ConAyuda,
  Fallo,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import { cantidad, contados, diaDeLima, soles, sumarDias } from '../formato';
import { mayuscula, useVocabulario } from '../vocabulario';

/**
 * Cuánto valía el almacén al cerrar un día.
 *
 * El valorizado del resumen es *a ahora*: sirve para operar y no para
 * contabilidad, porque mañana ya es otro número y el del 31 se perdió. Un
 * restaurante cierra sus libros por mes, y el almacén es parte de eso.
 *
 * **Sale de releer el kardex, no de una tabla de cierres.** El libro ya guarda
 * el saldo y el costo de cada movimiento, así que el último de cada insumo
 * hasta esa fecha es la foto de ese día: exacta por construcción, y sin nada
 * que mantener al día ni que se pueda desincronizar.
 */
export function Valorizacion() {
  const vocabulario = useVocabulario();
  const [fecha, setFecha] = useState(() => ayer());

  const consulta = useQuery({
    queryKey: ['valorizacion', fecha],
    queryFn: () => almacen.valuation(fecha),
  });

  const datos = consulta.data;
  const lineas = datos?.lines ?? [];

  const porCategoria = new Map<string, number>();
  for (const linea of lineas) {
    porCategoria.set(
      linea.category,
      (porCategoria.get(linea.category) ?? 0) + Number.parseFloat(linea.value),
    );
  }

  return (
    <>
      <Cabecera
        titulo="Valorización"
        subtitulo="Cuánto valía el almacén al cerrar un día" ayuda="Cuánto valía el almacén al terminar un día. Es lo que pide el contador para el cierre del mes."
        acciones={
          <>
            <div className="campo campo--en-linea">
              <Rotulo htmlFor="al-dia" ayuda="El día cuyo cierre quieres ver. Normalmente el último del mes, para la contabilidad.">Al cierre del</Rotulo>
              <input
                id="al-dia"
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </div>
            <Boton
              onClick={() =>
                descargar(
                  `/inventory/valuation.csv?at=${fecha}`,
                  `valorizacion-${fecha}.csv`,
                )
              }
              disabled={lineas.length === 0}
            >
              Exportar
            </Boton>
          </>
        }
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        {consulta.isLoading ? (
          <Cargando que="la valorización" />
        ) : lineas.length === 0 ? (
          <Vacio
            titulo={`El ${fecha} no había nada en el almacén`}
            mensaje={`Ningún ${vocabulario.insumo} tenía saldo al cerrar ese día. Si el local ya operaba, revisa que las entradas de entonces se hayan registrado: lo que no entró al libro no aparece aquí ni en ningún cierre.`}
          />
        ) : (
          <>
            <div className="cifras">
              <Tarjeta>
                <p className="versal">Valorizado al cierre</p>
                <p className="cifra__valor">{soles(datos?.total ?? '0')}</p>
                <p className="cifra__apoyo">
                  {contados(lineas.length, vocabulario.insumo, vocabulario.insumos)} con saldo
                </p>
              </Tarjeta>

              {[...porCategoria.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([categoria, valor]) => (
                  <Tarjeta key={categoria}>
                    <p className="versal">{categoria}</p>
                    <p className="cifra__valor">{soles(valor)}</p>
                    <p className="cifra__apoyo">
                      {Math.round(
                        (valor / Number.parseFloat(datos?.total ?? '1')) * 100,
                      )}{' '}
                      % del total
                    </p>
                  </Tarjeta>
                ))}
            </div>

            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>{mayuscula(vocabulario.insumo)}</th>
                    <th>Categoría</th>
                    <th className="numero">Cantidad</th>
                    <th className="numero"><ConAyuda ayuda={`El costo promedio que tenía el ${vocabulario.insumo} al cierre de ese día, sin IGV.`}>Costo unitario</ConAyuda></th>
                    <th className="numero"><ConAyuda ayuda={`Cantidad por costo: lo que valía ese ${vocabulario.insumo} al cierre.`}>Valorizado</ConAyuda></th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((linea) => (
                    <tr key={linea.supplyId}>
                      <td>
                        <div className="celda-nombre__texto">
                          <strong>{linea.name}</strong>
                          <span className="mono apagado">{linea.sku}</span>
                        </div>
                      </td>
                      <td className="apagado">{linea.category}</td>
                      <td className="numero mono">
                        {cantidad(linea.quantity)} {linea.unit}
                      </td>
                      <td className="numero apagado">{soles(linea.unitCost)}</td>
                      <td className="numero">{soles(linea.value)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={4}>
                      <strong>Total</strong>
                    </td>
                    <td className="numero">
                      <strong>{soles(datos?.total ?? '0')}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/**
 * Ayer, no hoy.
 *
 * Un cierre se hace del día que ya terminó: preguntar por hoy a media tarde da
 * una foto que va a cambiar antes de la cena, y quien la imprima estará
 * cuadrando contra un número que no existe desde hace dos horas.
 */
function ayer(): string {
  // El ayer de Lima: con UTC, pasadas las siete de la noche «ayer» era hoy.
  return sumarDias(diaDeLima(), -1);
}
